import { useAdminRouteV2ControllerUpdateRouteCustomerOrders } from "service";
import { createRoutesInvalidationPredicate } from "service/api/estabrookQueryKeys";
import { queryClient } from "@/query-client";

export interface UseRestoreSkippedOrders {
  restore: (locationIds: string[]) => Promise<void>;
  isRestoring: boolean;
}

/**
 * "Restore to Routes" - move selected skipped orders back to normal service.
 *
 * This only calls the order-status bulk endpoint (skipped -> regular). It
 * deliberately does NOT also call the driver-status bulk endpoint:
 *  - The order-status endpoint (customer-orders) only accepts routes whose
 *    shift state is "planning" (route.v2.bulk-orders.service.ts throws
 *    otherwise), which is also the only phase SkippedOrdersButton renders in.
 *  - The driver-status endpoint (driver-statuses) rejects "planning" routes
 *    outright - confirmed live: "In order to update driver statuses, the
 *    routes of the selected locations must be live or ended". So calling it
 *    here would always 400.
 *  - It's also unnecessary: flipping order status never touches
 *    driverStatus (see customer-orders.service.ts), and locations already
 *    sit at driverStatus "toBePlowed" by default during planning (driver
 *    statuses aren't meaningful until a route goes live). So the location is
 *    already effectively "to be plowed" once its order status is regular.
 */
const useRestoreSkippedOrders = (date: string): UseRestoreSkippedOrders => {
  const { mutateAsync: updateCustomerOrders, isLoading: isRestoring } =
    useAdminRouteV2ControllerUpdateRouteCustomerOrders();

  const restore = async (locationIds: string[]): Promise<void> => {
    await updateCustomerOrders({
      pathParams: { date },
      body: { locations: locationIds, status: "regular" },
    });
    // Route-planner data (map, table, sidebar) is a separate cached query -
    // without this it keeps showing the old "Skip" status until a refresh.
    await queryClient.invalidateQueries(createRoutesInvalidationPredicate());
  };

  return {
    restore,
    isRestoring,
  };
};

export default useRestoreSkippedOrders;
